import { createAction } from '@reduxjs/toolkit';
import {
  IAutoCheckout,
  IAutoEnrollmentApplication,
  IAutoPolicy,
  IAutoQuote,
  IAutoQuoteApplication,
  IAutoQuoteEnrollment,
  IAutoVariablesPayload,
  IOktaAccessToken,
  IOktaIdToken,
  IOktaLoginInfo,
  IOktaRefreshedTokens,
  IQuoteUUID,
  IRentersBreakUp,
  IRentersCheckout,
  IRentersCreateQuote,
  IRentersPolicy,
  IRentersQuote,
  IRentersQuoteApplication,
  IRentersQuoteDetails,
  IRentersQuotePlan,
  IRentersVariables,
  IRentersVariablesPayload,
  IUser,
  IVehicleModel,
  TPolicy,
  TPolicyHealthCheckErrorState,
} from '@sureapp/platform-sdk';

import { TAddEditVehicleFormValidationSchema } from '@@/auto/components/AddEditVehicleForm/AddEditVehicleFormValidationSchema.types';
import ACTION_TYPES from '@@/auto/constants/actionTypes';
import {
  IFeatureFlag,
  IQuoteData,
  IQuoteHealthCheckState,
} from '@@/auto/interfaces';

function handleFailuresAndErrors(error: any) {
  return {
    payload: error,
  };
}

/**
 * XXX: Setting up for Auto store
 * Enrollment Application (Auto)
 * =========================
 */
export const updateEnrollmentApplication = createAction<
  IAutoEnrollmentApplication
>(ACTION_TYPES.UPDATE_ENROLLMENT_APPLICATION);

export const clearEnrollmentApplication = createAction(
  ACTION_TYPES.CLEAR_ENROLLMENT_APPLICATION
);

/**
 * XXX: Setting up for Auto store
 * Clear Quote (Auto)
 * =========================
 */

export const clearAutoQuote = createAction(ACTION_TYPES.CLEAR_AUTO_QUOTE);

/**
 * XXX: Setting up for Auto store
 * Create Quote (Auto)
 * =========================
 */

export const createAutoQuoteRequest = createAction<IAutoQuoteEnrollment>(
  ACTION_TYPES.CREATE_AUTO_QUOTE
);

export const createAutoQuoteSuccess = createAction<IAutoQuote>(
  ACTION_TYPES.CREATE_AUTO_QUOTE_SUCCESS
);

export const createAutoQuoteFailure = createAction(
  ACTION_TYPES.CREATE_AUTO_QUOTE_FAILURE,
  handleFailuresAndErrors
);

/**
 * XXX: Setting up for Auto store
 * Get Quote (Auto)
 * =========================
 */

export const getAutoQuoteRequest = createAction<string>(
  ACTION_TYPES.GET_AUTO_QUOTE
);

export const getAutoQuoteSuccess = createAction<IAutoQuote>(
  ACTION_TYPES.GET_AUTO_QUOTE_SUCCESS
);

export const getAutoQuoteFailure = createAction(
  ACTION_TYPES.GET_AUTO_QUOTE_FAILURE,
  handleFailuresAndErrors
);

/**
 * XXX: Setting up for Auto store
 * Update Quote (Auto)
 * =========================
 */

export const updateAutoQuoteRequest = createAction<
  IAutoQuote | IAutoQuoteEnrollment
>(ACTION_TYPES.GET_AUTO_QUOTE);

export const updateAutoQuoteSuccess = createAction<IAutoQuote>(
  ACTION_TYPES.UPDATE_AUTO_QUOTE_SUCCESS
);

export const updateRentersQuoteRequest = createAction<{ quote: IRentersQuote }>(
  ACTION_TYPES.UPDATE_QUOTE
);

export const updateRentersQuoteSuccess = createAction<{ quote: IRentersQuote }>(
  ACTION_TYPES.UPDATE_QUOTE_SUCCESS
);

export const updateQuoteFailure = createAction(
  ACTION_TYPES.GET_AUTO_QUOTE_FAILURE,
  handleFailuresAndErrors
);

/**
 * XXX: Setting up for Auto store
 * Get Quote Vars (Auto)
 * =========================
 */

export const getAutoQuoteCheckoutRequest = createAction<{
  quoteNumber: string;
  application?: IAutoQuoteApplication;
}>(ACTION_TYPES.GET_AUTO_QUOTE_CHECKOUT);

export const getAutoQuoteCheckoutSuccess = createAction<IAutoCheckout>(
  ACTION_TYPES.GET_AUTO_QUOTE_CHECKOUT_SUCCESS
);

export const getAutoQuoteCheckoutFailure = createAction(
  ACTION_TYPES.GET_AUTO_QUOTE_CHECKOUT_FAILURE,
  handleFailuresAndErrors
);

/**
 * XXX: Setting up for Auto store
 * Get Vehicle Data (Auto)
 * =========================
 */

export const getVehiclesYearsRequest = createAction<string>(
  ACTION_TYPES.GET_VEHICLES_YEARS
);

export const getVehiclesYearsSuccess = createAction<number[]>(
  ACTION_TYPES.GET_VEHICLES_YEARS_SUCCESS
);

export const getVehiclesYearsFailure = createAction(
  ACTION_TYPES.GET_VEHICLES_YEARS_FAILURE,
  handleFailuresAndErrors
);

export const getVehiclesMakesRequest = createAction<{
  year: number;
  regionCode: string;
}>(ACTION_TYPES.GET_VEHICLES_MAKES);

export const getVehiclesMakesSuccess = createAction<string[]>(
  ACTION_TYPES.GET_VEHICLES_MAKES_SUCCESS
);

export const getVehiclesMakesFailure = createAction(
  ACTION_TYPES.GET_VEHICLES_MAKES_FAILURE,
  handleFailuresAndErrors
);

export const getVehiclesModelsRequest = createAction<{
  year: number;
  make: string;
  regionCode: string;
}>(ACTION_TYPES.GET_VEHICLES_MODELS);

export const getVehiclesModelsSuccess = createAction<IVehicleModel[]>(
  ACTION_TYPES.GET_VEHICLES_MODELS_SUCCESS
);

export const getVehiclesModelsFailure = createAction(
  ACTION_TYPES.GET_VEHICLES_MODELS_FAILURE,
  handleFailuresAndErrors
);

/**
 * XXX: Setting up for Auto store
 * Get Quote Checkout (Auto)
 * =========================
 */

export const getAutoQuoteVarsRequest = createAction<string>(
  ACTION_TYPES.GET_AUTO_QUOTE_VARS
);

export const getAutoQuoteVarsSuccess = createAction<IAutoVariablesPayload>(
  ACTION_TYPES.GET_AUTO_QUOTE_VARS_SUCCESS
);

export const getAutoQuoteVarsFailure = createAction(
  ACTION_TYPES.GET_AUTO_QUOTE_VARS_FAILURE,
  handleFailuresAndErrors
);

/**
 * XXX: Setting up for Auto store
 * Finalize Quote (Auto)
 * =========================
 */

export const finalizeAutoQuoteRequest = createAction<{
  quoteNumber: string;
  opts: { username?: string; password?: string };
}>(ACTION_TYPES.FINALIZE_AUTO_QUOTE);

export const finalizeAutoQuoteSuccess = createAction<IAutoQuote>(
  ACTION_TYPES.FINALIZE_AUTO_QUOTE_SUCCESS
);

export const finalizeAutoQuoteFailure = createAction(
  ACTION_TYPES.FINALIZE_AUTO_QUOTE_FAILURE,
  handleFailuresAndErrors
);

/**
 * XXX: Setting up for Auto store
 * Update Cached Quote Health Check (Auto)
 * =========================
 */

export const updateCachedQuoteHealthCheckState = createAction<
  IQuoteHealthCheckState
>(ACTION_TYPES.UPDATE_CACHED_QUOTE_HEALTH_CHECK_STATE);

/**
 * Update Policy Health Check Error Details (Auto)
 * =========================
 */
export const updatePolicyHealthCheckErrorDetails = createAction<
  TPolicyHealthCheckErrorState
>(ACTION_TYPES.UPDATE_POLICY_HEALTH_CHECK_ERROR_DETAILS);

export const clearPolicyHealthCheckErrorDetails = createAction(
  ACTION_TYPES.CLEAR_POLICY_HEALTH_CHECK_ERROR_DETAILS
);

/**
 * Update QuoteData store
 * =========================
 */

export const updateQuoteData = createAction<Partial<IQuoteData>>(
  ACTION_TYPES.UPDATE_QUOTE_DATA
);

/**
 * Create quote
 * =========================
 */

export const createQuoteRequest = createAction<IRentersCreateQuote>(
  ACTION_TYPES.CREATE_QUOTE
);

export const createQuoteSuccess = createAction<{ quote: IRentersQuote }>(
  ACTION_TYPES.CREATE_QUOTE_SUCCESS
);

export const createQuoteFailure = createAction(
  ACTION_TYPES.CREATE_QUOTE_FAILURE,
  handleFailuresAndErrors
);

/**
 * Get plans
 * =========================
 */

export const getPlansRequest = createAction<string>(ACTION_TYPES.GET_PLANS);

export const getPlansSuccess = createAction<{ plans: IRentersQuotePlan[] }>(
  ACTION_TYPES.GET_PLANS_SUCCESS
);

export const getPlansFailure = createAction(
  ACTION_TYPES.GET_PLANS_FAILURE,
  handleFailuresAndErrors
);

/**
 * Update quote
 * =========================
 */

export const updateQuoteRequest = createAction<IRentersQuoteApplication>(
  ACTION_TYPES.UPDATE_QUOTE
);
export const updateQuoteSuccess = createAction<{ quote: IRentersQuote }>(
  ACTION_TYPES.UPDATE_QUOTE_SUCCESS
);
export const updateRentersQuoteFailure = createAction(
  ACTION_TYPES.UPDATE_QUOTE_FAILURE,
  handleFailuresAndErrors
);

/**
 * Get quote vars
 * =========================
 */

export const getQuoteVarsRequest = createAction(ACTION_TYPES.GET_QUOTE_VARS);

export const getQuoteVarsSuccess = createAction<IRentersVariablesPayload>(
  ACTION_TYPES.GET_QUOTE_VARS_SUCCESS
);

export const getQuoteVarsFailure = createAction(
  ACTION_TYPES.GET_QUOTE_VARS_FAILURE,
  handleFailuresAndErrors
);

/**
 * Checkout quote
 * =========================
 */

export const checkoutQuoteRequest = createAction(ACTION_TYPES.CHECKOUT_QUOTE);

export const checkoutQuoteSuccess = createAction<IRentersCheckout>(
  ACTION_TYPES.CHECKOUT_QUOTE_SUCCESS
);

export const checkoutQuoteFailure = createAction(
  ACTION_TYPES.CHECKOUT_QUOTE_FAILURE,
  handleFailuresAndErrors
);

/**
 * Update quote details
 * =========================
 */

export const updateQuoteDetails = createAction<Partial<IRentersQuoteDetails>>(
  ACTION_TYPES.UPDATE_QUOTE_DETAILS
);

/**
 * Update variable selections
 * ==============================
 */

export const updateVariableSelections = createAction<
  Partial<IRentersVariables>
>(ACTION_TYPES.UPDATE_VARIABLE_SELECTIONS);

/**
 * Finalize quote
 * =========================
 */

export const finalizeQuoteRequest = createAction<{ uuid: IQuoteUUID }>(
  ACTION_TYPES.FINALIZE_QUOTE
);

export const finalizeQuoteSuccess = createAction<{ quote: IRentersQuote }>(
  ACTION_TYPES.FINALIZE_QUOTE_SUCCESS
);

export const finalizeQuoteFailure = createAction(
  ACTION_TYPES.FINALIZE_QUOTE_FAILURE,
  handleFailuresAndErrors
);

/**
 * Get policies
 * =========================
 */

export const getPoliciesRequest = createAction(ACTION_TYPES.GET_POLICIES);

export const getPoliciesSuccess = createAction<{ policies: IRentersPolicy[] }>(
  ACTION_TYPES.GET_POLICIES_SUCCESS
);

export const getPoliciesFailure = createAction(
  ACTION_TYPES.GET_POLICIES_FAILURE,
  handleFailuresAndErrors
);

/**
 * Get policy
 * =========================
 */

type TGetPolicyPayload<Policy extends TPolicy = TPolicy> = { policy: Policy };

export const getPolicyRequest = createAction(ACTION_TYPES.GET_POLICY);

export const getPolicySuccess = <Policy extends TPolicy = TPolicy>(
  payload: TGetPolicyPayload<Policy>
) =>
  createAction<TGetPolicyPayload<Policy>>(ACTION_TYPES.GET_POLICY_SUCCESS)(
    payload
  );
getPolicySuccess.toString = () => ACTION_TYPES.GET_POLICY_SUCCESS;
getPolicySuccess.type = ACTION_TYPES.GET_POLICY_SUCCESS;

export const getPolicyFailure = createAction(
  ACTION_TYPES.GET_POLICY_FAILURE,
  handleFailuresAndErrors
);

/**
 * Update policy
 * =========================
 */

export const updatePolicyRequest = createAction<{
  policy: TPolicy;
}>(ACTION_TYPES.UPDATE_POLICY);

export const updatePolicySuccess = createAction<{
  policy: TPolicy;
}>(ACTION_TYPES.UPDATE_POLICY_SUCCESS);

export const updatePolicyFailure = createAction(
  ACTION_TYPES.UPDATE_POLICY_FAILURE,
  handleFailuresAndErrors
);

/**
 * Get policy vars
 * =========================
 */

export const getPolicyVarsRequest = createAction<string>(
  ACTION_TYPES.GET_POLICY_VARS
);

export const getPolicyVarsSuccess = createAction<
  IRentersVariablesPayload | IAutoVariablesPayload
>(ACTION_TYPES.GET_POLICY_VARS_SUCCESS);

export const getPolicyVarsFailure = createAction(
  ACTION_TYPES.GET_QUOTE_VARS_FAILURE,
  handleFailuresAndErrors
);

/**
 * Get policy checkout
 * =========================
 */

export const getPolicyCheckoutRequest = createAction<{ policyId: string }>(
  ACTION_TYPES.GET_POLICY_CHECKOUT
);

export const getPolicyCheckoutSuccess = createAction<
  IRentersCheckout | IAutoCheckout
>(ACTION_TYPES.GET_POLICY_CHECKOUT_SUCCESS);

export const getPolicyCheckoutFailure = createAction(
  ACTION_TYPES.GET_POLICY_CHECKOUT_FAILURE,
  handleFailuresAndErrors
);

/**
 * Editing policy
 * =========================
 */

export const updateEditingPolicy = createAction<{
  policy: IRentersPolicy;
}>(ACTION_TYPES.UPDATE_EDITING_POLICY);

export const clearEditingPolicy = createAction(
  ACTION_TYPES.CLEAR_EDITING_POLICY
);

/**
 * Editing policy
 * =========================
 */

export const updateStagedAutoPolicyRequest = createAction<{
  policy: IAutoPolicy | null;
}>(ACTION_TYPES.UPDATE_STAGED_AUTO_POLICY);

export const clearStagedAutoPolicyRequest = createAction(
  ACTION_TYPES.CLEAR_STAGED_AUTO_POLICY
);

/**
 * Cancel policy
 * =========================
 */

export const cancelPolicyRequest = createAction<{ reason: string }>(
  ACTION_TYPES.CANCEL_POLICY
);
export const cancelPolicySuccess = createAction<{ policy: IRentersPolicy }>(
  ACTION_TYPES.CANCEL_POLICY_SUCCESS
);
export const cancelPolicyFailure = createAction(
  ACTION_TYPES.CANCEL_POLICY_FAILURE,
  handleFailuresAndErrors
);

/**
 * Save okta tokens
 * XXX: update types when adding okta types
 * =========================
 */

export const saveOktaIdToken = createAction<{
  token: IOktaIdToken;
  hasLoggedInDuringPurchaseFlow: boolean;
}>(ACTION_TYPES.SAVE_OKTA_ID_TOKEN);

export const saveOktaAccessToken = createAction<
  IOktaAccessToken,
  typeof ACTION_TYPES.SAVE_OKTA_ACCESS_TOKEN
>(ACTION_TYPES.SAVE_OKTA_ACCESS_TOKEN);

/**
 * Refresh okta tokens
 * XXX: update types when adding okta types
 * =========================
 */

export const refreshOktaTokensRequest = createAction(
  ACTION_TYPES.REFRESH_OKTA_TOKENS
);

export const refreshOktaTokensSuccess = createAction<
  (
    tokens: [IOktaIdToken, IOktaAccessToken]
  ) => { payload: IOktaRefreshedTokens }
>(ACTION_TYPES.REFRESH_OKTA_TOKENS_SUCCESS, ([idToken, accessToken]) => ({
  payload: {
    idToken,
    accessToken,
  },
}));

export const refreshOktaTokensFailure = createAction(
  ACTION_TYPES.REFRESH_OKTA_TOKENS_FAILURE,
  handleFailuresAndErrors
);

/**
 * Get the user
 * =========================
 */

export const getUserRequest = createAction(ACTION_TYPES.GET_USER);

export const getUserSuccess = createAction<IUser>(
  ACTION_TYPES.GET_USER_SUCCESS
);

export const getUserFailure = createAction(
  ACTION_TYPES.GET_USER_FAILURE,
  handleFailuresAndErrors
);

/**
 * Get feature flags
 * =========================
 */

export const getFeatureFlagsRequest = createAction(
  ACTION_TYPES.GET_FEATURE_FLAGS
);
export const getFeatureFlagsSuccess = createAction<IFeatureFlag[]>(
  ACTION_TYPES.GET_FEATURE_FLAGS_SUCCESS
);
export const getFeatureFlagsFailure = createAction(
  ACTION_TYPES.GET_FEATURE_FLAGS_FAILURE,
  handleFailuresAndErrors
);

/**
 * Update the user
 * =========================
 */

export const updateUserRequest = createAction<IUser>(ACTION_TYPES.UPDATE_USER);

export const updateUserSuccess = createAction<IUser>(
  ACTION_TYPES.UPDATE_USER_SUCCESS
);

export const updateUserFailure = createAction(
  ACTION_TYPES.UPDATE_USER_FAILURE,
  handleFailuresAndErrors
);

/**
 * Change user password
 * =========================
 */

export const updateUserPasswordRequest = createAction(
  ACTION_TYPES.UPDATE_USER_PASSWORD
);

export const updateUserPasswordSuccess = createAction(
  ACTION_TYPES.UPDATE_USER_PASSWORD_SUCCESS
);

export const updateUserPasswordFailure = createAction(
  ACTION_TYPES.UPDATE_USER_PASSWORD_FAILURE,
  handleFailuresAndErrors
);

/**
 * Create LevelCredit user
 * =========================
 */

export const createRenttrackUserRequest = createAction(
  ACTION_TYPES.CREATE_RENTTRACK_USER
);

export const createRenttrackUserSuccess = createAction(
  ACTION_TYPES.CREATE_RENTTRACK_USER_SUCCESS
);

export const createRenttrackUserFailure = createAction(
  ACTION_TYPES.CREATE_RENTTRACK_USER_FAILURE,
  handleFailuresAndErrors
);

/**
 * Send prior coverage termination
 * (Break up letter)
 * ===================================
 */

export const sendPriorCoverageTerminationRequest = createAction<
  Partial<IRentersBreakUp>
>(ACTION_TYPES.SEND_PRIOR_COVERAGE_TERMINATION);

export const sendPriorCoverageTerminationSuccess = createAction(
  ACTION_TYPES.SEND_PRIOR_COVERAGE_TERMINATION_SUCCESS
);

export const sendPriorCoverageTerminationFailure = createAction(
  ACTION_TYPES.SEND_PRIOR_COVERAGE_TERMINATION_FAILURE,
  handleFailuresAndErrors
);

/**
 * Okta login info after finalizing quote
 * =========================
 */
export const createOktaLoginInfo = createAction<IOktaLoginInfo>(
  ACTION_TYPES.CREATE_OKTA_LOGIN_INFO
);

export const clearOktaLoginInfo = createAction<IOktaLoginInfo>(
  ACTION_TYPES.CLEAR_OKTA_LOGIN_INFO
);

/**
 * Exchange activation token
 * =========================
 */

export const exchangeActivationTokenRequest = createAction<{
  token: string | null;
}>(ACTION_TYPES.EXCHANGE_ACTIVATION_TOKEN);

export const exchangeActivationTokenSuccess = createAction<any>(
  ACTION_TYPES.EXCHANGE_ACTIVATION_TOKEN_SUCCESS
);

export const exchangeActivationTokenFailure = createAction(
  ACTION_TYPES.EXCHANGE_ACTIVATION_TOKEN_FAILURE,
  handleFailuresAndErrors
);

/**
 * Logout
 * XXX: update types when adding okta types
 * =========================
 */

export const logout = createAction(ACTION_TYPES.LOGOUT);

/**
 * CCPA Banner
 * =========================
 */

export const closeCcpaBanner = createAction(ACTION_TYPES.CLOSE_CCPA_BANNER);

/**
 * Check rating address eligibility
 * =========================
 */

export const checkRatingAddressEligibilityRequest = createAction<{
  policyId: string;
}>(ACTION_TYPES.CHECK_RATING_ADDRESS_ELIGIBILITY);

export const checkRatingAddressEligibilitySuccess = createAction<any>(
  ACTION_TYPES.CHECK_RATING_ADDRESS_ELIGIBILITY_SUCCESS
);

export const checkRatingAddressEligibilityFailure = createAction(
  ACTION_TYPES.CHECK_RATING_ADDRESS_ELIGIBILITY_FAILURE,
  handleFailuresAndErrors
);

/**
 * CMS
 * =========================
 */

export const getCmsContentRequest = createAction(ACTION_TYPES.GET_CMS_CONTENT);

export const getCmsContentSuccess = createAction<any>(
  ACTION_TYPES.GET_CMS_CONTENT_SUCCESS
);

export const getCmsContentFailure = createAction(
  ACTION_TYPES.GET_CMS_CONTENT_FAILURE,
  function prepare(error) {
    return {
      payload: {
        slug: error.reason.slug,
        error: {
          ...error.reason,
          messsage: 'S3 failed to return content.',
        },
      },
    };
  }
);

/**
 * User logged in during Purchace Flow
 * =========================
 */
export const updateHasLoggedInDuringPurchaseFlow = createAction<boolean>(
  ACTION_TYPES.UPDATE_HAS_LOGGED_IN_DURING_PF
);

/**
 * Clear Okta tokens for QA flows
 * =========================
 */
export const clearOktaForQA = createAction<null>(
  ACTION_TYPES.CLEAR_OKTA_FOR_QA
);

/**
 * Exchange renters quote auth token
 * ===================================
 */

export const exchangeRentersQuoteAuthTokenRequest = createAction<{
  quoteNumber: string;
  token: string;
}>(ACTION_TYPES.EXCHANGE_RENTERS_QUOTE_AUTH_TOKEN);

export const exchangeRentersQuoteAuthTokenSuccess = createAction<string>(
  ACTION_TYPES.EXCHANGE_RENTERS_QUOTE_AUTH_TOKEN_SUCCESS
);

export const exchangeRentersQuoteAuthTokenFailure = createAction(
  ACTION_TYPES.EXCHANGE_RENTERS_QUOTE_AUTH_TOKEN_FAILURE,
  handleFailuresAndErrors
);

/**
 * Exchange auto quote auth token
 * ===================================
 */

export const exchangeAutoQuoteAuthTokenRequest = createAction<{
  quoteNumber: string;
  token: string;
}>(ACTION_TYPES.EXCHANGE_AUTO_QUOTE_AUTH_TOKEN);

export const exchangeAutoQuoteAuthTokenSuccess = createAction<string>(
  ACTION_TYPES.EXCHANGE_AUTO_QUOTE_AUTH_TOKEN_SUCCESS
);

export const exchangeAutoQuoteAuthTokenFailure = createAction(
  ACTION_TYPES.EXCHANGE_AUTO_QUOTE_AUTH_TOKEN_FAILURE,
  handleFailuresAndErrors
);

/**
 * Quote Assist
 * ===================================
 */

export const updateIsAgentUsingQuoteAssist = createAction<boolean>(
  ACTION_TYPES.UPDATE_IS_AGENT_USING_QUOTE_ASSIST
);

/**
 * Stashed AddEditVehicleForm values
 * ====================================
 */
export const updateStashedAddEditVehicleFormValues = createAction<
  TAddEditVehicleFormValidationSchema
>(ACTION_TYPES.UPDATE_STASHED_ADD_EDIT_VEHICLE_FORM_VALUES);

export const clearStashedAddEditVehicleFormValues = createAction(
  ACTION_TYPES.CLEAR_STASHED_ADD_EDIT_VEHICLE_FORM_VALUES
);
